import { useRef } from "react";
import { Reveal, Tag, H2, GoldBtn } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import CursorGrid from "@/components/MagicBento/CursorGrid";
import FoldText from "@/components/FoldText/FoldText";
import TiltedCard from "@/components/TiltedCard/TiltedCard";

const steps = [
  { n: "01", img: "/jack-step1.jpg", title: "Tell Jack What Happened", copy: "Start with your claim situation. Was it denied? Delayed? Underpaid? Confusing? Jack will guide you from there." },
  { n: "02", img: "/jack-step2.jpg", title: "Get Plain-English Guidance", copy: "Jack helps explain what may be happening, what questions to ask, and what mistakes to avoid." },
  { n: "03", img: "/jack-step3.jpg", title: "Decide Your Next Move", copy: "You may continue with Ask Jack, run the Rip-Off Detector, report your claim, or explore premium assistance when appropriate." },
];

const questions = [
  "Why did my insurance company deny my claim?",
  "Is this settlement offer too low?",
  "What should I ask my adjuster?",
  "What does this denial letter mean?",
  "What should I avoid saying to the insurance company?",
  "When should I consider professional help?",
];

export const HowItWorks = () => {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();

  return (
  <section id="how-it-works" data-testid="how-it-works-section" className="relative bg-[#0A0A0C] py-24 md:py-32">
    <GlobalSpotlight
      gridRef={gridRef}
      disableAnimations={isMobile}
      glowColor="255, 209, 102"
      spotlightRadius={280}
    />
    <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
      <Reveal>
        <Tag testId="how-tag">How It Works</Tag>
        <H2>
          <FoldText
            text="Get clarity in three simple steps."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />
        </H2>
      </Reveal>
      <div ref={gridRef} className="bento-section mt-14 grid md:grid-cols-3 gap-5">
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.12}>
            <ParticleCard
              testId={`step-card-${i}`}
              disableAnimations={isMobile}
              glowColor="255, 209, 102"
              className="magic-bento-card magic-bento-card--border-glow group relative h-full bg-[#1A1A1D] border border-white/10 hover:border-[#FFD166]/40 transition-colors duration-300 overflow-hidden"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #1A1A1D 2%, transparent 45%)" }} />
                <span className="absolute bottom-2 left-6 font-display font-bold text-6xl text-[#FFD166] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                  {s.n}
                </span>
              </div>
              <div className="p-7 pt-4">
                <h3 className="font-display text-lg tracking-wide text-white">{s.title}</h3>
                <p className="mt-3 text-sm text-[#A1A1AA] font-light leading-relaxed">{s.copy}</p>
              </div>
            </ParticleCard>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.3}>
        <p className="mt-10 font-serif-jack italic text-xl text-[#FFD166]/80">
          You leave with more clarity, more confidence, and a better understanding of your options.
        </p>
      </Reveal>
    </div>
  </section>
  );
};

export const AskJack = () => {
  const isMobile = useMobileDetection();

  return (
  <section id="ask-jack" data-testid="ask-jack-section" className="relative bg-[#121214] py-24 md:py-32 overflow-hidden">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(700px circle at 50% 100%, rgba(138,43,226,0.12), transparent 60%)" }}
    />
    <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
      <Reveal>
        <Tag testId="ask-jack-tag">Ask Jack</Tag>
        <H2>
          <FoldText
            text="Ask Jack your first question."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />
        </H2>
        <p className="mt-3 font-serif-jack italic text-2xl text-[#9D4EDD]">
          Get guidance before you make your next claim decision.
        </p>
        <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
          Not sure whether your denial is valid? Confused by a letter from your insurance company?
          Worried your settlement is too low? Ask Jack. Your first question helps you understand
          what may be happening and what next step might make sense.
        </p>
      </Reveal>
      <div className="mt-10 flex flex-wrap gap-3">
        {questions.map((q, i) => (
          <Reveal key={q} delay={i * 0.06}>
            <button
              type="button"
              data-testid={`example-question-${i}`}
              onClick={() => window.AIWidget?.ask(q)}
              className="inline-block border border-[#8A2BE2]/40 bg-[#8A2BE2]/[0.06] px-4 py-2.5 text-sm text-white/80 font-light hover:border-[#9D4EDD] hover:bg-[#8A2BE2]/[0.14] transition-colors duration-200 cursor-pointer"
            >
              {q}
            </button>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.35}>
        <div className="mt-10 flex flex-wrap items-center gap-5">
          <GoldBtn testId="ask-jack-cta" onClick={() => window.AIWidget?.ask()}>ASK JACK</GoldBtn>
          <span className="font-mono-file text-[11px] tracking-[0.2em] text-[#71717A]">
            START WITH ONE QUESTION. NO PRESSURE. NO HARD SELL.
          </span>
        </div>
      </Reveal>
      </div>

      <Reveal delay={0.2} className="lg:col-span-5">
        <div className="relative max-w-[400px] mx-auto lg:ml-auto">
          <button
            type="button"
            onClick={() => window.AIWidget?.ask()}
            aria-label="Ask Jack"
            className="block w-full cursor-pointer"
          >
            <TiltedCard
              imageSrc="/jack-invites.jpg"
              altText="Jack inviting your first question"
              testId="ask-jack-visual"
              containerHeight="480px"
              containerWidth="100%"
              imageHeight="480px"
              imageWidth="100%"
              scaleOnHover={1.05}
              rotateAmplitude={8}
              showMobileWarning={false}
              showTooltip={false}
              className="tilted-card-figure--square"
              innerClassName="overflow-hidden border border-[#8A2BE2]/50 shadow-[0_40px_100px_-20px_rgba(138,43,226,0.45)] after:absolute after:inset-0 after:content-[''] after:pointer-events-none after:bg-[linear-gradient(to_bottom,rgba(10,10,12,0.85),transparent_30%,transparent_70%,rgba(10,10,12,0.7))]"
              imgClassName="object-[50%_30%]"
              overlayContent={
                <>
                  <p className="font-hand text-2xl text-[#FFD166]">Go ahead. Ask me about your claim.</p>
                  <p className="mt-1 font-mono-file text-[9px] tracking-[0.25em] text-white/60">
                    JACK D. HAPSBURG · CLAIM INTELLIGENCE
                  </p>
                </>
              }
              displayOverlayContent
              overlayClassName="w-full h-full flex flex-col justify-end p-4"
            />
          </button>
          <div className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-[#FFD166]/60" />
          <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-[#FFD166]/60" />
        </div>
      </Reveal>
    </div>
  </section>
  );
};
